import { sendDataAPI } from '@lib/sendDataAPI';
import { useRouter } from 'next/router';
import React, { createContext, useContext, useEffect, useState } from 'react';

interface SessionContextProps {
	children: React.ReactNode;
	initialContext: string;
	onLoad?: () => void;
	onError?: (error: string) => void;
}

const SessionContext = createContext({
	context: '',
	user: {
		siteId: '',
		frontUrl: '',
		isPaidPlan: false,
		billingLink: '',
		storeId: 0,
		email: '',
		storeName: '',
		createdAt: '',
		planName: '',
		isNotConfigured: true,
	},
	isLoading: true,
});

export interface InstanceDataResponse extends Response {
	data?: {
		siteId: string;
		appId: string;
		instanceId: string;
		frontUrl: string;
		isPaidPlan: boolean;
		billingLink: string;
		storeId: number;
		email: string;
		storeName: string;
		createdAt: string;
		planName: string;
		isNotConfigured: boolean;
	};
	message?: string;
}

const SessionProvider: React.FC<SessionContextProps> = ({ children, initialContext, onLoad, onError }) => {
	const { query } = useRouter();

	const [context, setContext] = useState(initialContext || '');
	const [isLoading, setIsLoading] = useState(true);
	const [user, setUser] = useState({
		siteId: '',
		frontUrl: '',
		isPaidPlan: false,
		billingLink: '',
		storeId: 0,
		email: '',
		storeName: '',
		createdAt: '',
		planName: '',
		isNotConfigured: true,
	});

	useEffect(() => {
		const fetchAccess = async () => {
			try {
				let response: InstanceDataResponse = await sendDataAPI({ jwtToken: context }, '/api/instance/get_info', 'GET');
				response = await response.json();

				if (response.data) {
					setUser({
						siteId: response.data.siteId || '',
						frontUrl: response.data.frontUrl || '',
						isPaidPlan: response.data.isPaidPlan || false,
						billingLink: `https://www.wix.com/apps/upgrade/${response.data.appId}?appInstanceId=${response.data.instanceId}`,
						storeId: response.data.storeId || 0,
						email: response.data.email || '',
						storeName: response.data.storeName || '',
						createdAt: response.data.createdAt || '',
						planName: response.data.planName || '',
						isNotConfigured: response.data.isNotConfigured,
					});
				} else {
					throw new Error('No data received from API');
				}
			} catch (err) {
				console.error(err);
				if (onError) {
					onError('Something went wrong... Please, try again');
				}
			} finally {
				setIsLoading(false);
				if (onLoad) {
					onLoad();
				}
			}
		};

		fetchAccess();

		if (query.instance) {
			setContext(query.instance as string);
		}
	}, [query.instance, context, onLoad, onError]);

	if (isLoading) {
		return null;
	}

	return <SessionContext.Provider value={{ context, user, isLoading }}>{children}</SessionContext.Provider>;
};

export const useSession = () => useContext(SessionContext);

export default SessionProvider;
